import {Component, HostListener, inject} from '@angular/core';
import {BaseModalComponent} from "../../base-modal/base-modal.component";
import {MatButton, MatIconButton, MatMiniFabButton} from "@angular/material/button";
import {MAT_DIALOG_DATA, MatDialog} from "@angular/material/dialog";
import {PreviewFileDialogData} from "../types/preview-file-modal.types";
import {DomSanitizer} from "@angular/platform-browser";
import {MatIcon} from "@angular/material/icon";
import {PreviewFilesStateService} from "../../../../services/preview-files-state.service";
import {PreviewFileModalService} from "../services/preview-file-modal.service";
import {UpdateFieldCommentFile} from "../../../../types/file.types";
import {isHypothesisFileType} from "../../hypothesis-modal/configs/hypothesis-modal-config";
import {FormsModule} from "@angular/forms";
import {MatFormField, MatInput} from "@angular/material/input";
import {MatLabel} from "@angular/material/form-field";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {EditorTextModalComponent} from "../../editor-text-modal/editor-text-modal.component";
import {ConfirmationStatus} from "../../../../types/confirmation-modal.types";
import {PreviewFileService} from "./services/preview-file.service";
import {previewFileModalName} from "../configs/preview-file-config";

@Component({
  selector: 'app-preview-file-modal',
  standalone: true,
  imports: [
    BaseModalComponent,
    MatButton,
    MatIcon,
    MatIconButton,
    FormsModule,
    MatLabel,
    MatIcon,
    MatInput,
    MatFormField,
    MatMiniFabButton,
    CdkTextareaAutosize
  ],
  templateUrl: './preview-file-modal.component.html',
  styleUrl: './preview-file-modal.component.scss'
})
export class PreviewFileModalComponent extends BaseModalComponent {
  public previewFileDialogData = inject<PreviewFileDialogData>(MAT_DIALOG_DATA);
  private sanitizer: DomSanitizer = inject(DomSanitizer);
  private previewFilesStateService: PreviewFilesStateService = inject(PreviewFilesStateService);
  private previewFileModalService: PreviewFileModalService = inject(PreviewFileModalService);
  private previewFileService: PreviewFileService = inject(PreviewFileService);
  private dialog: MatDialog = inject(MatDialog);

  public fileUrl: string = '';
  public comment: string | null = null;
  public isNeedCommentField: boolean = false;
  private currentIsEditMode: boolean = false;
  private isDestroyed = false;

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (!this.previewFileDialogData?.isNeedArrow || this.isDestroyed) return;

    const direction = this.previewFileModalService.navigationMap.get(event.key);
    if (direction) {
      event.preventDefault();
      this.navigateFile(direction);
    }
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.modalStatesService.openModal(previewFileModalName);
    this.initialize();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(previewFileModalName);
    this.isDestroyed = true;
    this.editModeService.setEditMode(this.currentIsEditMode);
  }

  public getSafeUrl(url: string) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url + '#toolbar=0&navpanes=0');
  }

  public navigateFile(direction: -1 | 1): void {
    if (!this.previewFileDialogData.fileType || !this.previewFileDialogData.fileId) { return; }

    const newFileId = this.previewFileModalService.getNewFileId({
      fileId: this.previewFileDialogData.fileId,
      fileType: this.previewFileDialogData.fileType,
      direction: direction
    });

    this.fileUrl = this.previewFilesStateService.getFileUrl(newFileId);
    this.previewFileDialogData.fileId = newFileId;
    this.comment = this.previewFileService.getComment(this.previewFileDialogData);
  }

  public async handleCommentChange(): Promise<void> {
    const updateFieldCommentFile: UpdateFieldCommentFile = {
      id: this.previewFileService.getHypothesisFileDataId(this.previewFileDialogData),
      comment: this.comment
    };

    await this.previewFileService.updateHypothesisFileData(updateFieldCommentFile);
  }

  public async handleOpenFullscreenText(): Promise<void> {
    const textDialogData = this.previewFileService.setTextDialogData(this.previewFileDialogData);
    this.dialog.open(EditorTextModalComponent, {
      width: '800px',
      data: textDialogData
    }).afterClosed().subscribe((result?: string | number | ConfirmationStatus) => {
      if (result !== undefined && result !== 'cancel') {
        if (!isHypothesisFileType(this.previewFileDialogData.fileType)) { return; }

        this.previewFileService.setHypothesisFileData({
          type: this.previewFileDialogData.fileType,
          fileId: this.previewFileDialogData.fileId || 0,
          comment: result.toString()
        });
        this.comment = result.toString();
      }
    });
  }

  private initialize(): void {
    this.currentIsEditMode = this.isEditModeActive;
    this.fileUrl = this.previewFileDialogData.url;
    this.isNeedCommentField = isHypothesisFileType(this.previewFileDialogData.fileType);
    this.comment = this.previewFileService.getComment(this.previewFileDialogData);
  }
}
